<template>
	<v-row>
		<v-col>

			<ui-box :form="$refs.kickoffDateForm" new-forms>
				<v-row>
					<v-col>
						<span class="grey--text text--darken-1 text-body-2">
								Places in your pass have submitted the following things to do within the 
								pass. You may choose to accept or decline them.
							</span>
					</v-col>
				</v-row>

				<v-divider class="mb-10 mt-4 opacity-80"></v-divider>

				<div class="d-flex position-relative tab-btns z-2">

					<tab-button
							:active="status === 'pending'"
							:small="$vuetify.breakpoint.smAndDown"
							@click="changeStatus('pending')"
					>
						Pending
						<v-icon class="ms-1 me-n1" size="20">
							{{ getStatusCountIcon('pending') }}
						</v-icon>
					</tab-button>

					<tab-button
							:active="status === 'approved'"
							:small="$vuetify.breakpoint.smAndDown"
							@click="changeStatus('approved')"
					>
						Approved
						<v-icon class="ms-1 me-n1" size="20">
							{{ getStatusCountIcon('approved') }}
						</v-icon>
					</tab-button>

					<tab-button
							:active="status === 'declined'"
							:small="$vuetify.breakpoint.smAndDown"
							@click="changeStatus('declined')"
					>
						Rejected
						<v-icon class="ms-1 me-n1" size="20">
							{{ getStatusCountIcon('declined') }}
						</v-icon>
					</tab-button>

				</div>

				<div class="mt-4 rounded-lg tabs-content text-body-2">

					<template v-if="currentProduct">
						<v-data-table
								hide-default-footer
								hide-default-header
						>
							<template v-slot:body>
								<tr>
									<td class="font-weight-bold table-label">Place</td>
									<td class="table-info">
										{{ currentProduct.venueName }}
									</td>
								</tr>
								<tr>
									<td class="font-weight-bold table-label">Type</td>
									<td class="table-info text-capitalize">
										{{ currentProduct.productType }}
									</td>
								</tr>
								<tr>
									<td class="font-weight-bold table-label">Name</td>
									<td class="table-info" v-if="isCheckIn || status !== 'pending'">
										{{ currentProduct.name }}
									</td>
									<td v-else class="table-info">
										<a :href="editProductUrl(currentProduct)"
											 target="_blank"
										>{{ currentProduct.name }}</a>
									</td>
								</tr>
								<tr>
									<td class="font-weight-bold table-label">Description</td>
									<td class="table-info html-content">
										<span v-html="currentProduct.short_information"></span>
									</td>
								</tr>
								<tr>
									<td class="font-weight-bold table-label">Restrictions</td>
									<td class="table-info html-content">
										<span v-html="currentProduct.restrictions"></span>
									</td>
								</tr>
								<tr>
									<td
											class="font-weight-bold table-label"
											:class="{ 'no-line': status === 'pending' }"
									>Submitted On
									</td>
									<td
											class="table-info"
											:class="{ 'no-line': status === 'pending' }"
									>
										{{
											moment(currentProduct.submittedDate).format('MMMM Do YYYY @ hh:mm a')
										}}
									</td>
								</tr>
								<tr v-if="['declined', 'pending'].includes(status)">
									<td class="no-line" colspan="2">
										<v-divider/>
									</td>
								</tr>
								<tr v-if="['declined', 'pending'].includes(status)">
									<td class="font-weight-bold no-line table-label">
										<span style="line-height: 1.5rem;">Add To Past?</span>
									</td>
									<td class="no-line table-info">
										<v-radio-group
												v-model="currentProduct.included"
												:key="`product-${currentProduct.id}`"
												class="mt-0 pt-0"
												:disabled="pass.no_orders"
												hide-details
												@change="v => { 
													return currentProduct.included_date = v === 0
															? pass.created_at
															: null
												}"
										>
											<v-radio
													class="mt-0 pt-0"
													dense
													hide-details
													:value="null"
											>
												<template v-slot:label>
												<span class="text-body-2">
													Add this item to future orders.
												</span>
												</template>
											</v-radio>
											<v-radio
													class="mt-0 pt-0"
													dense
													hide-details
													:value="0"
											>
												<template v-slot:label>
												<span class="text-body-2">
													Add this item to past and future orders. 
													{{ pass.no_orders ? ' (No existing orders)' : '' }}
												</span>
												</template>
											</v-radio>
										</v-radio-group>
									</td>
								</tr>
								<tr v-if="['declined', 'pending'].includes(status) && currentProduct.included === 0">
									<td class="no-line" colspan="2">

										<!-- Paid Item - Past -->
										<v-alert
												v-if="!isFreePass"
												class="mb-4 mt-0 rounded-lg"
												border="left"
												color="warning"
												dense
												icon="mdi-alert-circle"
												text
										>
											<span class="text-body-2">
												<span class="font-weight-bold">Warning: </span>
												By adding this offer to past and future orders, you will be 
												responsible to pay out the merchant for any usage of this pass. 
												Before proceeding, we recommend you contact Bandwango to 
												re-evaluate the pricing model and understand if you should raise 
												the price of your pass by adding this new offer.
											</span>
										</v-alert>
										<!-- Paid Item - Past - END -->

										<!-- Free Item - Past -->
										<v-alert
												class="mb-n4 mt-0 rounded-lg"
												border="left"
												color="info"
												dense
												icon="mdi-information-slab-circle"
												text
										>
											<span class="text-body-2">
												<span class="font-weight-bold">
													Processing Time for Adding to Past Order: 
												</span>
												Adding a product to past orders will take time for our system to 
												process.
											</span>
										</v-alert>
										<!-- Free Item - Past - END -->

									</td>
								</tr>
							</template>
						</v-data-table>
					</template>

					<template v-else>
						<span class="font-italic">
							No {{ status !== 'declined' ? status : 'rejected' }} products.
						</span>
					</template>
				</div>

				<template v-if="showProductNav">

					<v-row class="mt-0">
						<v-col>
							<div class="d-flex justify-space-between">
								<div>
									<template v-if="localProducts[status].length > 1">
										<v-btn
												class="me-1"
												color="grey darken-2"
												icon
												outlined
												@click="prevProduct"
										>
											<v-icon>mdi-chevron-left</v-icon>
										</v-btn>
										<v-btn
												color="grey darken-2"
												icon
												outlined
												@click="nextProduct"
										>
											<v-icon>mdi-chevron-right</v-icon>
										</v-btn>
									</template>
								</div>
								<div v-if="['declined', 'pending'].includes(status) && localProducts[status].length">
									<v-btn
											v-if="status === 'pending'"
											class="me-2 rounded-lg"
											color="grey darken-1"
											depressed
											outlined
											@click="declineProduct"
									>
										Reject
									</v-btn>
									<v-btn
											class="rounded-lg"
											color="primary"
											depressed
											:loading="isLoading"
											@click="approveProduct"
									>
										Approve
									</v-btn>
								</div>
							</div>
						</v-col>
					</v-row>

				</template>

			</ui-box>

		</v-col>

		<v-col class="mt-8 mt-sm-4 mt-md-0" cols="12" md="auto">
			<v-responsive
					:aspect-ratio="1/2"
					min-width="calc(min(calc(100vw), calc(420px)) - 2rem)"
					max-width="420px"
			>
				<mobile-pass-preview
						:loading-message="currentProduct ? 'Loading Preview' : 'Nothing To Preview.'"
						:preview-url="currentProduct?.previewUrl"
				></mobile-pass-preview>
			</v-responsive>
		</v-col>

	</v-row>
</template>

<script>
import draggable from 'vuedraggable';
import { passHelpers } from "../../../helpers";

export default {
	name: "Submissions",
	components: {
		draggable
	},
	props: {
		extraData: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		places: {
			type: Array,
			default: () => []
		},
		products: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			currentIndex: 0,
			isLoading: false,
			localProducts: cloneDeep(this.products),
			status: 'pending'
		};
	},
	mounted() {
		const setIncluded = product => {
			this.$set(product, 'included', !this.isFreePass || this.pass.no_orders ? null : 0);
		};
		this.localProducts.pending.forEach(setIncluded);
		this.localProducts.declined.forEach(setIncluded);
	},
	computed: {
		currentProduct() {
			const product = this.localProducts[this.status][this.currentIndex];
			return product
					? {
						...product,
						included_date: product?.included === 0 ? this.pass.created_at : null
					}
					: null;
		},

		isFreePass() {
			return this.pass.template?.settings?.paid === false;
		},

		isCheckIn() {
			return this.currentProduct.productType === 'check-in';
		},

		loadingMessage() {
			if(this.currentProduct) return `Loading preview for: ${this.currentProduct.name}`;
			return `Nothing to preview for: ${this.status}.`;
		},

		productsByStatus() {
			return this.localProducts[this.status];
		},

		showApprove() {

		},
		showProductNav() {
			return !!this.localProducts[this.status].length;
		}
	},
	methods: {
		approveProduct() {
			this.isLoading = true;
			const url = `/partner-portal/${this.partner.id}/passes/approve-product-submission/${this.currentProduct.campaignAssignmentId}`;

			axios.post(url, this.currentProduct)
					.then(() => {
						this.isLoading = false;
						this.moveProduct('approved');
					})
					.catch(error => {
						this.isLoading = false;
						console.log(error);
					});
		},

		changeStatus(status) {
			this.status = status;
			this.currentIndex = 0;
		},

		declineProduct() {
			this.isLoading = true;
			const url = `/partner-portal/${this.partner.id}/passes/decline-product-submission/${this.currentProduct.campaignAssignmentId}`;

			axios.post(url)
					.then(() => {
						this.isLoading = false;
						this.moveProduct('declined');
					})
					.catch(error => {
						this.isLoading = false;
						console.log(error);
					});
		},

		editProductUrl(product) {
			return `/partner-portal/${this.partner.id}/products/edit-product/${product.id}`
		},

		getButtonColor(status) {
			return this.status === status
					? 'secondary lighten-1'
					: 'secondary'
		},

		getPreviewLink() {
			if(!this.currentProduct || this.localProducts[this.status][this.currentIndex].previewUrl) return;
			// if venue status is VENUE_APPROVED
			const submParam = this.currentProduct.venueStatus === 4 ? '' : `&submissionVenueId=${this.currentProduct.venueId}`;

			this.isLoading = true;
			const url = `/partner-portal/${this.partner.id}/passes/get-preview-link/${this.currentProduct.campaignAssignmentId}`;

			axios.get(url)
					.then(({ data }) => {
						this.isLoading = false;
						this.$set(this.localProducts[this.status][this.currentIndex], 'previewUrl', `${data}${submParam}`);
					})
					.catch(error => {
						this.isLoading = false;
						console.log(error);
					});
		},

		getStatusCountIcon(status) {
			const count = this.localProducts[status].length;
			const countIcon = count >= 10 ? '9-plus' : count;
			return `mdi-numeric-${countIcon}-circle`;
		},

		moveProduct(targetStatus) {
			const productToMove = this.currentProduct;

			// Remove the product from the current status
			this.localProducts[this.status].splice(this.currentIndex, 1);

			// Adjust currentIndex if necessary
			if(this.currentIndex >= this.localProducts[this.status].length) {
				this.currentIndex = this.localProducts[this.status].length - 1;
			}

			this.$nextTick(() => {
				// Add the product to the target status
				this.localProducts[targetStatus].push(productToMove);
				window.EventBus.$emit('get-updated-menu');
				this.getPreviewLink();
			});
		},

		nextProduct() {
			const maxIndex = this.localProducts[this.status].length - 1;
			if(this.currentIndex === maxIndex) this.currentIndex = 0;
			else this.currentIndex += 1;
		},

		prevProduct() {
			const maxIndex = this.localProducts[this.status].length - 1;
			if(this.currentIndex === 0) this.currentIndex = maxIndex;
			else this.currentIndex -= 1;
		}
	},
	watch: {
		currentIndex: {
			immediate: true,
			handler() {
				if(this.currentProduct)
					this.getPreviewLink();
			}
		},
		status: {
			immediate: true,
			handler() {
				this.getPreviewLink();
			}
		}
	}
}
</script>


<style lang="scss" scoped>
.v-data-table > .v-data-table__wrapper > table {
	& > tr {
		& > td {
			font-size: 0.875rem;
			padding-bottom: 1rem;
			padding-top: 1rem;
			position: relative;
			vertical-align: top;
		}

		&:not(:last-of-type) > td:not(.no-line) {
			&::after {
				content: '';
				position: absolute;
				height: 1px;
				background: rgba(0, 0, 0, 0.04);
				bottom: -1px;
			}

			&:first-child::after {
				left: 0;
				width: calc(100% - 1rem);
			}

			&:not(:first-child):not(:last-child)::after {
				left: 1rem;
				width: calc(100% - 2rem);
			}

			&:nth-child(2):last-child::after {
				left: 1rem;
				width: calc(100% - 1rem);
			}

			&:last-child::after {
				right: 0;
				white-space: nowrap;
				width: 100%;
			}
		}
	}
}

.tab-btns {
	& > button.tab-btn--active:first-child::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: -1px;
		height: 30px;
		width: 1px;
		background: linear-gradient(to top, #e7e7e7, transparent);
	}
}

.table-label {
	padding-right: 1rem;
	white-space: nowrap;
	width: 1%;
}

.table-info {
	border-left: solid 1px rgba(0, 0, 0, 0.08);
	padding-left: 1rem;
}

.tabs-content {
	border: solid 1px rgba(0, 0, 0, 0.096);
	padding: 1rem;
	position: relative;
	top: -1px;
	z-index: 1;
}
</style>
<style lang="scss">
.table-info.html-content {
	p {
		color: inherit !important;
	}
}
</style>
